// Tournament Match Scheduler (113)
//
// A single-elimination bracket layered over a tournament Event. The bracket
// owns its rounds; the matches are ordinary Match rows (linked by
// Match.tournament_round_id + bracket_position). Slots reference Team by id;
// names are derived fresh at read time (open Q6 -- no denormalised snapshot).

import type { EventVenuePublic } from "./venue.js";

export const TOURNAMENT_BRACKET_TYPES = ["single_elim"] as const;
export type TournamentBracketType = (typeof TOURNAMENT_BRACKET_TYPES)[number];

export const TOURNAMENT_SEEDING_RULES = ["manual", "random"] as const;
export type TournamentSeedingRule = (typeof TOURNAMENT_SEEDING_RULES)[number];

export const TOURNAMENT_BRACKET_STATUSES = ["draft", "published", "running", "concluded"] as const;
export type TournamentBracketStatus = (typeof TOURNAMENT_BRACKET_STATUSES)[number];

export const TOURNAMENT_BRACKET_SIZES = [4, 8, 16, 32] as const;
export type TournamentBracketSize = (typeof TOURNAMENT_BRACKET_SIZES)[number];

/** A team on the event roster that can be seeded into the bracket (per-team). */
export interface TournamentSeedTeam {
  team_id: string;
  name: string; // team.name, joined fresh
  school_name: string;
}

/** One side of a bracket match: a team, a bye, or an as-yet-unknown winner. */
export interface TournamentSlot {
  team_id: string | null; // null = TBD (awaiting an earlier round) or a bye
  name: string | null; // team.name joined fresh; null when TBD/bye
  school_name: string | null;
  is_bye: boolean; // true when this side is an empty bye slot
  winner: boolean; // true once the match is final and this side won
}

export interface TournamentMatchPublic {
  match_id: string;
  position: number; // 0-based index within the round
  starts_at: string; // ISO
  venue_label: string | null;
  home: TournamentSlot;
  away: TournamentSlot;
  home_score: number | null;
  away_score: number | null;
  final: boolean;
}

export interface TournamentRoundPublic {
  id: string;
  index: number; // 1 = first round
  name: string;
  match_count: number;
  matches: TournamentMatchPublic[];
}

export interface TournamentBracketPublic {
  id: string;
  event_id: string;
  bracket_type: TournamentBracketType;
  bracket_size: number;
  seeding_rule: TournamentSeedingRule;
  status: TournamentBracketStatus;
}

/** GET /events/:id/tournament -- public (anonymous-allowed, open Q5). */
export interface TournamentResponse {
  bracket: TournamentBracketPublic | null; // null = no bracket created yet
  rounds: TournamentRoundPublic[]; // empty until published
  entered_teams: TournamentSeedTeam[]; // seedable pool (event roster teams)
  // 194 -- lightweight event summary so the public tournament page can render
  // the hosting venue (with directions) + participating schools, matching the
  // authenticated event header. `venues` sorted primary-first.
  event: {
    name: string;
    venues: EventVenuePublic[];
    schools: { school_id: string; name: string; is_host: boolean }[];
  };
  _actions: { can_manage: boolean };
}
